import { loadUiSetting, saveUiSetting } from '@/lib/ui-settings'
import type { ListProjectsParams } from './types/project.types'

/**
 * AP-35 Nachtrag 5 — was der Navigationsrahmen im Projektkopf sich merkt.
 *
 * **Zuletzt geöffnet** folgt dem Benutzer (UI-Einstellung `recent_projects`,
 * serverseitig wie die Listen-Layouts, AP-23). Gemerkt werden Nummer und
 * Bezeichnung mit, damit Suchfeld und ↶ ohne eigene Abfrage etwas anzeigen; beim
 * nächsten Öffnen wird der Eintrag aufgefrischt.
 *
 * **Die Listenansicht** (Filter und Sortierung, mit denen die Projektliste
 * zuletzt lief) ist Arbeitskontext und bleibt lokal — dieselbe Einordnung wie die
 * zuletzt getippten Filterwerte in `ui-settings.ts`. Nach ihr richtet sich das
 * Blättern.
 */

export interface RecentProject {
  id: string
  projectNumber: string
  name: string
}

const RECENT_KEY = 'recent_projects'
const RECENT_MAX = 10

export function loadRecentProjects(): RecentProject[] {
  const roh = loadUiSetting<unknown>(RECENT_KEY, [])
  return Array.isArray(roh)
    ? roh.filter((p): p is RecentProject => !!p && typeof (p as RecentProject).id === 'string')
    : []
}

/** Stellt das geöffnete Projekt an den Anfang; gibt die neue Liste zurück. */
export function rememberRecentProject(p: RecentProject): RecentProject[] {
  const alt = loadRecentProjects()
  if (alt[0]?.id === p.id && alt[0].projectNumber === p.projectNumber && alt[0].name === p.name) return alt
  const neu = [p, ...alt.filter((x) => x.id !== p.id)].slice(0, RECENT_MAX)
  saveUiSetting(RECENT_KEY, neu)
  return neu
}

/** Ein Projekt, das nicht (mehr) geöffnet werden kann, fällt aus dem Verlauf. */
export function forgetRecentProject(id: string): void {
  const alt = loadRecentProjects()
  if (alt.some((x) => x.id === id)) saveUiSetting(RECENT_KEY, alt.filter((x) => x.id !== id))
}

// ── Listenansicht ───────────────────────────────────────────────────────────

const LIST_CONTEXT_PREFIX = 'symphenia.ui.projects.listcontext'

export type ListContext = Omit<ListProjectsParams, 'page' | 'limit'>

export function rememberListContext(userId: string | undefined, ctx: ListContext): void {
  try {
    localStorage.setItem(`${LIST_CONTEXT_PREFIX}.${userId ?? 'anon'}`, JSON.stringify(ctx))
  } catch {
    // ignorieren — ohne Merker blättert der Kopf nach Projektnummer
  }
}

export function loadListContext(userId: string | undefined): ListContext {
  try {
    const roh = localStorage.getItem(`${LIST_CONTEXT_PREFIX}.${userId ?? 'anon'}`)
    const ctx = roh ? (JSON.parse(roh) as ListContext) : null
    if (ctx && typeof ctx === 'object') return ctx
  } catch {
    // ignorieren
  }
  return {}
}
